import { Collapse, Spin, Tag } from "antd";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { FaCheck, FaTimes } from "react-icons/fa";
import {
    formatCurrency,
    getCatalogDailyPrice,
} from "../../../lib/insuranceSaleApi";

const QuoteBreakdown = ({ quote, t }) => (
    <div className="mt-4 space-y-2 rounded-lg border border-gray-100 bg-gray-50 p-4 text-sm">
        <div className="flex justify-between">
            <span className="text-gray-500">{t("wizard.dailyRate")}</span>
            <span className="font-medium">{formatCurrency(quote.dailyRate)}</span>
        </div>
        <div className="flex justify-between">
            <span className="text-gray-500">{t("wizard.days")}</span>
            <span className="font-medium">{quote.billableDays}</span>
        </div>
        <div className="flex justify-between">
            <span className="text-gray-500">{t("wizard.subtotal")}</span>
            <span className="font-medium">{formatCurrency(quote.subtotal)}</span>
        </div>
        <div className="flex justify-between">
            <span className="text-gray-500">{t("wizard.taxes")}</span>
            <span className="font-medium">{formatCurrency(quote.taxTotal)}</span>
        </div>
        <div className="flex justify-between border-t border-gray-200 pt-2 text-base">
            <span className="font-semibold">{t("wizard.total")}</span>
            <span className="font-semibold">{formatCurrency(quote.total)}</span>
        </div>
    </div>
);

QuoteBreakdown.propTypes = {
    quote: PropTypes.object.isRequired,
    t: PropTypes.func.isRequired,
};

const getHeaderPriceLabel = ({ product, isSelected, quote, quoteLoading, carWorthValue, t }) => {
    if (isSelected && quoteLoading) {
        return t("wizard.pricing");
    }

    if (isSelected && quote) {
        return `${formatCurrency(quote.total)} / ${quote.billableDays} ${t("wizard.days").toLowerCase()}`;
    }

    const catalogPrice = getCatalogDailyPrice(product, carWorthValue);
    if (catalogPrice != null) {
        return t("wizard.fromPrice", { amount: formatCurrency(catalogPrice) });
    }

    return t("wizard.selectToPrice");
};

const InsuranceProductAccordion = ({
    products,
    selectedProductId,
    onSelectProduct,
    quote,
    quoteLoading,
    carWorthValue,
}) => {
    const { t } = useTranslation("sellInsurance");

    const items = products.map((product) => {
        const isSelected = product.id === selectedProductId;
        const priceLabel = getHeaderPriceLabel({
            product,
            isSelected,
            quote,
            quoteLoading,
            carWorthValue,
            t,
        });

        return {
            key: product.id,
            label: (
                <div className="flex w-full flex-col items-start gap-2 pr-0 sm:flex-row sm:items-center sm:justify-between sm:gap-3 sm:pr-2">
                    <div className="min-w-0 w-full sm:w-auto">
                        <div className="flex flex-wrap items-center gap-2">
                            <span className="text-sm font-semibold text-gray-900">{product.name}</span>
                            {product.coverageType ? (
                                <Tag className="m-0 uppercase">{product.coverageType}</Tag>
                            ) : null}
                            {isSelected ? <Tag color="#5D5DF6">{t("wizard.selected")}</Tag> : null}
                        </div>
                    </div>
                    <div className="flex w-full flex-shrink-0 items-center gap-2 text-sm font-semibold text-[#5D5DF6] sm:w-auto sm:justify-end">
                        {isSelected && quoteLoading ? <Spin size="small" /> : null}
                        <span className="break-words">{priceLabel}</span>
                    </div>
                </div>
            ),
            children: (
                <div>
                    {product.desc ? <p className="mb-3 text-sm text-gray-600">{product.desc}</p> : null}

                    {product.featureList?.length > 0 ? (
                        <div>
                            <p className="mb-2 text-sm font-medium text-gray-700">{t("wizard.features")}</p>
                            <div className="space-y-2">
                                {product.featureList.map((feature, index) => (
                                    <div key={index} className="flex items-start gap-2 text-sm">
                                        <span className="mt-0.5 flex-shrink-0">
                                            {feature.isActive ? (
                                                <FaCheck color="green" />
                                            ) : (
                                                <FaTimes color="red" />
                                            )}
                                        </span>
                                        <span className="flex-1 break-words">{feature.text}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : null}

                    {isSelected && quote && !quoteLoading ? <QuoteBreakdown quote={quote} t={t} /> : null}
                </div>
            ),
        };
    });

    return (
        <Collapse
            accordion
            activeKey={selectedProductId}
            onChange={(key) => {
                const nextKey = Array.isArray(key) ? key[0] : key;
                if (nextKey) {
                    onSelectProduct(nextKey);
                }
            }}
            items={items}
            className="[&_.ant-collapse-header]:!items-start [&_.ant-collapse-header]:!py-3"
        />
    );
};

InsuranceProductAccordion.propTypes = {
    products: PropTypes.arrayOf(PropTypes.object).isRequired,
    selectedProductId: PropTypes.string,
    onSelectProduct: PropTypes.func.isRequired,
    quote: PropTypes.object,
    quoteLoading: PropTypes.bool,
    carWorthValue: PropTypes.number,
};

InsuranceProductAccordion.defaultProps = {
    selectedProductId: undefined,
    quote: null,
    quoteLoading: false,
    carWorthValue: undefined,
};

export default InsuranceProductAccordion;
